import {
  Box,
  Chip,
  CircularProgress,
  debounce,
  Grid,
  Stack,
  SvgIcon,
  TablePagination,
  Typography,
  // useMediaQuery,
  // useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { ReactComponent as Close } from 'assets/images/ic_mini_close.svg';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { useNavigate } from 'react-router-dom';
import { AISystemsService } from 'services/AISystemService';
import DateFnsAdapter from '@date-io/date-fns';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { IRiskRegister } from 'modules/risk-management/risk-register/List';
import DialogBulkUpload from 'modules/ai-inventory/DialogBulkUpload';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { Formik } from 'formik';

const dateFns = new DateFnsAdapter();

export interface IAISystem {
  _id: string;
  id: string;
  sourceType: string;
  classification: string;
  solutionName: string;
  solutionOwner: string;
  organizationUnit: string;
  organizationSubUnit: string;
  stakeholder: string;
  legalEntity: string;
  userCustomerName: string;
  businessProcesses: string;
  uses: string;
  purpose: string;
  businessGoals: string;
  businessValue: string;
  platformName: string;
  riskTier: string;
  openSource: boolean;
  aiModelTypes: any[];
  technicalDocumentationLink: string;
  vendorName: string;
  consumptionMethod: string;
  environment: string;
  watermarkingTechnique: string;
  lifecycleStage: string;
  versionControlMethod: string;
  decisionCapability: string;
  buildDate: string;
  lastTrainingDate: string;
  approvalStatus: string;
  tenantId: any;
  createdBy: any;
  updatedBy: any;
  updatedDate: any;
  isActive: boolean;
  deletedAt: any;
  createdDate: string;
  __v: number;
  datasetId: IDataset[];
  riskRegisters: IRiskRegister[];
  isConverted: boolean;
  isPublic: boolean;
  isPublicApprovalStatus: string;
  updatedAt: any;
  publicSubmissionMail: any;
  createdAt: any;
}

const AISystemRecorder = (): React.ReactElement => {
  const dispatch = useDispatch();
  const aISystemPagination = useSelector(
    (state: IAppState) => state.paginationState.aISystem
  );

  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const handleDelete = useCallback(() => {}, []);
  // const theme = useTheme();
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [records, setRecords] = useState<IAISystem[]>([]);
  const navigation = useNavigate();
  // const match = useMediaQuery(theme.breakpoints.down('lg'));
  // API Calls
  const loadAISystems = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: String(aISystemPagination.offset),
      limit: String(aISystemPagination.pageLimit),
      solutionName: aISystemPagination.search,
      approvalstatus: aISystemPagination.status,
    }).toString();

    AISystemsService.getAISystems(queryString)
      .then((response) => {
        const { aiSystems, pagination } = response.data.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(aiSystems.map((d: any) => ({ ...d, id: d._id })));
        dispatch(
          PaginationActions.setPagination({
            aISystem: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  // Instance Methods
  const handlePageChange = (_: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        aISystem: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        aISystem: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        aISystem: {
          ...aISystemPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          aISystem: {
            ...aISystemPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [aISystemPagination]
  );

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        aISystem: {
          offset: 1,
          search: '',
          status: '',
        },
      })
    );
  };

  // Hooks
  useEffect(() => {
    loadAISystems();
  }, [
    aISystemPagination.offset,
    aISystemPagination.pageLimit,
    aISystemPagination.search,
    aISystemPagination.status,
  ]);

  // UI Elements

  return (
    <Box className="system-recorder-wrapper">
      <Grid
        container
        className="system-record-nav"
        spacing={{ xs: 2, md: 2 }}
        alignItems={{ xs: 'flex-start', xl: 'center' }}
      >
        <Grid item xs={12} xl={4}>
          <Typography variant="h3">AI System of Record</Typography>
        </Grid>

        <Grid item xs={12} xl={8}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            className="stack-record"
            justifyContent="flex-end"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            width="100%"
          >
            {false && (
              <Chip
                className="filter-by-chip"
                label="Filtered by: Under Review"
                variant="outlined"
                onDelete={handleDelete}
                deleteIcon={
                  <SvgIcon
                    component={Close}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
              />
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              className="stack-record-button"
              width={{ xs: '100%', sm: 'auto' }}
            >
              {orgACLConfig.aiInventory?.includes(
                'aiInventory.aiSystem.aiInventory.bulkUpload'
              ) && (
                <AppPrimaryButtonOutlined
                  title="Bulk Upload"
                  startIcon={
                    <SvgIcon
                      component={Upload}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                  onClick={() => {
                    setOpenBulk(true);
                  }}
                />
              )}

              <AppPrimaryButtonOutlined
                title="Detect Shadow AI"
                startIcon={
                  <SvgIcon
                    component={Search}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
                onClick={() => {
                  navigation('/discovery-ai');
                }}
              />

              {orgACLConfig.aiInventory?.includes(
                'aiInventory.aiSystem.aiInventory.addAddAISolution'
              ) && (
                <AppPrimaryButton
                  title="Add AI Solution"
                  startIcon={
                    <SvgIcon
                      component={Plus}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                  onClick={() => {
                    navigation('/ai-systems/add');
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Formik
        initialValues={{
          'search-aiSystem': aISystemPagination.search,
          status: aISystemPagination.status,
        }}
        enableReinitialize
        onSubmit={() => console.log('search-aiSystem')}
      >
        {({ resetForm }) => (
          <Grid
            container
            className="aiSyatem-list-nav"
            spacing={2}
            alignItems="flex-start"
          >
            <Grid item xs={12} sm={6} md={7}>
              <Box width="100%">
                <AppFormikTextField
                  inputProps={{
                    startAdornment: (
                      <SvgIcon
                        component={Search}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#76A81F"
                      />
                    ),
                  }}
                  outerLabel={false}
                  placeholder="Search by name.."
                  name="search-aiSystem"
                  onChange={(e: any) => {
                    debouncSearch(e.target.value);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <Box width="100%" sx={{ mt: { xs: 0, sm: 2 } }}>
                <AppFormikSelect
                  name="status"
                  className="rework-selection"
                  notched={false}
                  labelId="fix-selection-label"
                  testid="solicitor-dropdown"
                  MenuProps={{
                    className: 'menu-fix-selection single-fix-rework',
                  }}
                  placeholder="Filter Status"
                  notEmphasized
                  notSelectablePlaceholder={false}
                  items={[
                    'All Status',
                    'Approved',
                    'Pending',
                    'Draft',
                    'Archived',
                    'Decommissioned',
                  ]}
                  onChange={(e: any) => {
                    handleStatusChange(e.target.value);
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <AppPrimaryButtonOutlined
                fullWidth
                title="Clear Filters"
                sx={{
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  resetForm({
                    values: {
                      'search-aiSystem': '',
                      status: '',
                    },
                  });
                  handleClearFilter();
                }}
              />
            </Grid>
          </Grid>
        )}
      </Formik>

      {isLoadingRecords && (
        <Box className="empty-content">
          <CircularProgress />
          <EmptyPlaceholder />
        </Box>
      )}
      {!isLoadingRecords && records.length > 0 && (
        <Box className="table-system-record">
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                    Name
                  </TableCell>
                  <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                    Risk level
                  </TableCell>
                  <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                    Status
                  </TableCell>
                  <TableCell align="left">Created</TableCell>
                  <TableCell align="left">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
                      '&:last-child td, &:last-child th': {
                        border: 0,
                      },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.id}
                    </TableCell>
                    <TableCell
                      align="left"
                      component="th"
                      scope="row"
                      className="word-break"
                    >
                      {row.solutionName}
                    </TableCell>
                    <TableCell align="left">{row.riskTier}</TableCell>
                    <TableCell align="left">{row.approvalStatus}</TableCell>
                    <TableCell align="left">
                      {dateFns.date(row.createdAt).toDateString()}
                    </TableCell>
                    <TableCell align="left">
                      {orgACLConfig.aiInventory?.includes(
                        'aiInventory.aiSystem.aiInventory.view'
                      ) && (
                        <AppPrimaryButton
                          title="View"
                          isLoading={false}
                          disabled={false}
                          className="primary-light-fill btn-extra-small"
                          onClick={() => {
                            navigation(`/ai-systems/records/${row.id}`);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {!isLoadingRecords && records.length === 0 && (
        <Box className="app-plain-card empty-content">
          <EmptyPlaceholder
            icon={
              <img color="gray" width={50} src="/images/icons/ic_system.svg" />
            }
            title="No AI solutions yet"
            message="Get started by adding your first AI solution to the inventory"
          />
        </Box>
      )}
      {openBulk && (
        <DialogBulkUpload
          openBulk={openBulk}
          onClose={(reload: boolean) => {
            if (reload) {
              loadAISystems();
            }
            setOpenBulk(false);
          }}
          title="Bulk upload AI inventory"
          variant="aisystem"
        />
      )}

      {!isLoadingRecords && records.length > 0 && (
        <TablePagination
          component="div"
          count={aISystemPagination.totalRecords}
          page={aISystemPagination.offset - 1}
          rowsPerPage={aISystemPagination.pageLimit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          disabled={isLoadingRecords}
        />
      )}
    </Box>
  );
};

export default AISystemRecorder;
